'use client';

import { trackToolCompleted } from '@/helpers/toolCompletion';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import { HiOutlineExclamation } from '@react-icons/all-files/hi/HiOutlineExclamation';
import { HiOutlineCheckCircle } from '@react-icons/all-files/hi/HiOutlineCheckCircle';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledIssuesList,
  StyledHeadingTree,
  StyledHeadingNode,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
} from './HeadingStructureTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
}

interface IHeading {
  level: number;
  text: string;
}

interface IIssue {
  type: 'missing-h1' | 'multiple-h1' | 'skipped-level' | 'empty-heading';
  message: string;
}

interface IToolResult {
  headings: IHeading[];
  issues: IIssue[];
  status: 'poor' | 'fair' | 'good';
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

function extractHeadings(input: string): IHeading[] {
  const headings: IHeading[] = [];

  // Try HTML mode first
  const htmlPattern = /<(h[1-6])[^>]*>([\s\S]*?)<\/h[1-6]>/gi;
  let htmlMatch;
  let foundHtml = false;

  while ((htmlMatch = htmlPattern.exec(input)) !== null) {
    const level = parseInt(htmlMatch[1][1], 10);
    const text = htmlMatch[2].replace(/<[^>]+>/g, '').trim();
    if (text) {
      headings.push({ level, text });
      foundHtml = true;
    }
  }

  if (foundHtml) return headings;

  // Markdown-style fallback
  const lines = input.split('\n');
  for (const line of lines) {
    const mdMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (mdMatch) {
      headings.push({ level: mdMatch[1].length, text: mdMatch[2].trim() });
    }
  }

  return headings;
}

function analyzeHeadings(headings: IHeading[]): IIssue[] {
  const issues: IIssue[] = [];

  const h1Count = headings.filter((h) => h.level === 1).length;

  if (h1Count === 0) {
    issues.push({ type: 'missing-h1', message: 'No H1 found. Every page should have exactly one H1 tag.' });
  } else if (h1Count > 1) {
    issues.push({ type: 'multiple-h1', message: `Multiple H1 tags found (${h1Count}). Use exactly one H1 per page.` });
  }

  // Check for skipped levels
  let previousLevel = 0;
  for (const heading of headings) {
    if (previousLevel > 0 && heading.level > previousLevel + 1) {
      issues.push({
        type: 'skipped-level',
        message: `Heading level skipped: H${previousLevel} followed by H${heading.level} ("${heading.text.slice(0, 60)}${heading.text.length > 60 ? '...' : ''}")`,
      });
    }
    previousLevel = heading.level;
  }

  // Check empty headings (already filtered, but handle edge case)
  const emptyCheck = /<(h[1-6])[^>]*>\s*<\/h[1-6]>/gi;
  // We can only detect in original input; skip here as extraction already filters

  return issues;
}

function getWidgetDescription(slug: string): string {
  switch (slug) {
    case 'accordion':
      return 'Accordion FAQ sections add natural H3-H4 structure to your pages. Well-organized heading hierarchies improve both user experience and SEO topical coverage.';
    case 'tabs':
      return 'Tab widgets let you break content into structured sections with clear subheadings. Each tab can reinforce a different H2 topic on your page.';
    case 'comparison-tables':
      return 'Comparison tables use semantic table headers that complement your heading hierarchy. They help search engines understand structured, scannable content.';
    case 'feeds':
      return 'Content feeds add fresh H2 and H3 content to your pages dynamically. Regular new headings signal active, topically broad pages to search engines.';
    default:
      return 'Improve your content structure and SEO with powerful content widgets.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'accordion': return 'Add Accordion Free →';
    case 'tabs': return 'Add Tabs Free →';
    case 'comparison-tables': return 'Add Tables Free →';
    case 'feeds': return 'Add Feeds Free →';
    default: return 'Try It Free →';
  }
}

export const HeadingStructureTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [content, setContent] = useState('');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleAnalyze = () => {
    const trimmed = content.trim();
    if (!trimmed) {
      setError('Please paste your HTML or text content to analyze.');
      return;
    }
    setError('');

    const headings = extractHeadings(trimmed);

    if (headings.length === 0) {
      setError('No headings found. Paste HTML with H1-H6 tags, or markdown text with # heading syntax.');
      return;
    }

    const issues = analyzeHeadings(headings);

    let status: 'poor' | 'fair' | 'good';
    if (issues.length === 0) {
      status = 'good';
    } else if (issues.length <= 2) {
      status = 'fair';
    } else {
      status = 'poor';
    }

    setResult({ headings, issues, status });
    setHasCalculated(true);
    trackToolCompleted('result');
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const statusLabel =
    result?.status === 'good' ? 'Good' : result?.status === 'fair' ? 'Fair' : 'Poor';

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Heading Structure</span> Analyzer</h1>
        <p>
          Paste your HTML or markdown and instantly see your H1-H6 hierarchy.
          Catch missing H1s, skipped levels, and empty headings. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Analyze Your Heading Structure</h2>

          <StyledFormGroup>
            <label htmlFor="content">HTML or Markdown Content</label>
            <textarea
              id="content"
              placeholder={`Paste your page HTML or markdown here...\n\nExample HTML: <h1>Page Title</h1><h2>Section</h2>\nExample markdown: # Page Title\n## Section`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <p className="hint">Accepts raw HTML with h1-h6 tags, or markdown with # heading syntax.</p>
          </StyledFormGroup>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleAnalyze}>
            Analyze Heading Structure
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <div className="result-summary">
                <div className="summary-left">
                  <p className="result-label">Heading Structure</p>
                  <span className={`result-badge ${result.status}`}>{statusLabel}</span>
                </div>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-value">{result.headings.length}</span>
                    <span className="stat-label">Headings</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{result.headings.filter(h => h.level === 1).length}</span>
                    <span className="stat-label">H1 tags</span>
                  </div>
                  <div className="stat">
                    <span className={`stat-value ${result.issues.length > 0 ? 'has-issues' : 'no-issues'}`}>
                      {result.issues.length}
                    </span>
                    <span className="stat-label">Issues</span>
                  </div>
                </div>
              </div>

              {result.issues.length > 0 && (
                <StyledIssuesList>
                  <h3>Issues Found</h3>
                  {result.issues.map((issue, i) => (
                    <div key={i} className="issue-item">
                      <HiOutlineExclamation className="issue-icon" />
                      <p>{issue.message}</p>
                    </div>
                  ))}
                </StyledIssuesList>
              )}

              {result.issues.length === 0 && (
                <div className="result-good">
                  <HiOutlineCheckCircle />
                  <p><strong>No issues found.</strong> Your heading structure is clean and well-organized.</p>
                </div>
              )}

              <StyledHeadingTree>
                <h3>Heading Hierarchy</h3>
                {result.headings.map((heading, i) => (
                  <StyledHeadingNode key={i} level={heading.level}>
                    <span className="heading-level">H{heading.level}</span>
                    <span className="heading-text">{heading.text}</span>
                  </StyledHeadingNode>
                ))}
              </StyledHeadingTree>
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Build Better Heading Structures</h2>
              <p className="subtitle">
                These widgets help you add well-structured, SEO-rich content sections to any page.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free SEO Tools</h2>
            <p className="subtitle">
              More free tools to optimize every element of your pages.
            </p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/seo/${tool.slug}`}
                >
                  <span className="tool-icon"><HiOutlineSearch /></span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}
      </StyledToolBody>
    </>
  );
};
